import React, { useEffect, useMemo, useState } from 'react';
import { Download, Loader2, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import { SectionSummary } from './SectionSummary';
import { PendingLinesTable } from './PendingLinesTable';

const download = (blob, name) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
};

/** Section-wise pending vs received — every open PO line (or all, incl. completed), with Excel export. */
export const PendingReport = ({ sections }) => {
  const [f, setF] = useState({ section: 'all', status: 'open', vendor: '', q: '' });
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const params = useMemo(() => ({ status: f.status, ...(f.section !== 'all' ? { section: f.section } : {}),
    ...(f.vendor.trim() ? { vendor: f.vendor.trim() } : {}), ...(f.q.trim() ? { q: f.q.trim() } : {}) }), [f]);
  useEffect(() => {
    const t = setTimeout(() => {
      setD(null);
      apiClient.get('/po-followup/pending', { params }).then((r) => setD(r.data))
        .catch((e) => { setD({ rows: [], summary: [] }); toast.error(e.response?.data?.detail || 'Could not load the report'); });
    }, 300);
    return () => clearTimeout(t);
  }, [params]);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const exportXlsx = async () => {
    setBusy(true);
    try {
      const r = await apiClient.get('/po-followup/pending/export', { params, responseType: 'blob' });
      download(r.data, `po_pending_vs_received_${d?.today || new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.success('Excel downloaded');
    } catch (e) { toast.error('Export failed'); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="po-pending-report">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={f.section} onValueChange={set('section')}>
          <SelectTrigger className="w-[170px]" data-testid="po-pending-section"><SelectValue placeholder="Section" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sections</SelectItem>
            {sections.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={f.status} onValueChange={set('status')}>
          <SelectTrigger className="w-[190px]" data-testid="po-pending-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="open">Pending lines only</SelectItem>
            <SelectItem value="all">All lines (incl. completed)</SelectItem>
          </SelectContent>
        </Select>
        <Input value={f.vendor} onChange={(e) => set('vendor')(e.target.value)} placeholder="Supplier" className="w-[180px]" data-testid="po-pending-vendor" />
        <div className="relative flex-1 min-w-[180px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input value={f.q} onChange={(e) => set('q')(e.target.value)} placeholder="PO no, item or sort no" className="pl-9" data-testid="po-pending-search" />
        </div>
        <Button variant="outline" onClick={exportXlsx} disabled={busy || !d?.rows?.length} data-testid="po-pending-export">
          {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}Excel</Button>
      </div>
      {!d ? <Loader2 className="mx-auto my-10 h-6 w-6 animate-spin text-gray-400" /> : (
        <>
          <SectionSummary summary={d.summary} active={f.section} onPick={set('section')} />
          <PendingLinesTable rows={d.rows} />
        </>
      )}
    </div>
  );
};
